/**
 * Los ajustes de la PÁGINA pública de una oferta.
 *
 * ── Por qué está en el panel y no en el editor ───────────────────────────────
 * Toda oferta tiene ya su dirección: la de pago se compra en
 * `/checkout/buy/<slug>`, la gratuita se reclama en `/checkout/free/` y un
 * servicio con agenda se reserva en `/book/<slug>`. Son páginas de verdad
 * —se comparten por WhatsApp, se pegan en una historia, las lee Google— pero no
 * son páginas del constructor: no se pueden abrir en el editor, así que lo que
 * cualquier otra página sí decide —cómo se titula fuera, qué se ve al pegar el
 * enlace, si se indexa— no tenía dónde configurarse.
 *
 * Podría haberse metido la oferta en el editor. No: quien vende un PDF no entra
 * a un constructor de páginas para ponerle un título de SEO, y la oferta se
 * crea, se cobra y se mide aquí. Se queda donde vive.
 *
 * ── Vacío hereda, y por eso los marcadores de posición importan ──────────────
 * Ningún campo es obligatorio y casi ninguno se va a rellenar: lo normal es
 * crear una oferta y compartirla sin abrir esto. Vacío no es «sin título», es
 * «el de la oferta», y el marcador de posición enseña exactamente lo que se va a
 * usar —en negro, no en gris— para que se lea como el valor que es. Es la misma
 * regla que sigue el servidor al imprimir la cabecera (`offerPageHead`).
 *
 * ── Un borrador y un «Guardar» ───────────────────────────────────────────────
 * A diferencia del resto de la ficha —donde lo que se pulsa pasa—, aquí se
 * escribe: guardar en cada tecla mandaría una petición por letra y dejaría
 * títulos a medias en la página pública. Así que hay borrador, la barra de
 * arriba aparece cuando hay algo sin guardar y salir con cambios avisa, igual
 * que en el resto del panel.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  IconCheck,
  IconCopy,
  IconFileText,
  IconPhoto,
  IconPhotoPlus,
  IconSearch,
  IconWorld,
} from '@tabler/icons-react'
import { InfoHint, ToggleRow } from '../../SettingsControls'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { isFree } from './offerShared'
import { updateOffer, type Offer, type OfferPagePatch } from '../../../lib/api'
import { useToast } from '../../Toast'
import { translateServerError, useI18n } from '../../../lib/i18n'

/** Lo que Google recorta. El contador avisa; no impide escribir más. */
const TITLE_LIMIT = 60
const DESCRIPTION_LIMIT = 160

/**
 * La descripción de la oferta, tal y como se va a imprimir si aquí no se
 * escribe otra.
 *
 * Un párrafo de oferta trae saltos de línea y sangrías —se escribe en un área
 * de texto, a veces se pega desde otro sitio— y una etiqueta `<meta>` es una
 * sola línea. Pegarlo crudo dejaba el campo con su texto partido y con barra de
 * desplazamiento dentro, y la vista previa con un renglón cortado a media
 * palabra: se leía como algo roto cuando sólo era el mismo texto sin aplanar.
 * El servidor lo aplana igual antes de imprimirlo (`offerPageHead`).
 */
function inheritedDescription(description: string): string {
  return description.replace(/\s+/g, ' ').trim().slice(0, DESCRIPTION_LIMIT)
}

/** El borrador: los nueve ajustes más la dirección, que se guarda con ellos. */
type Draft = OfferPagePatch & { slug: string }

/**
 * Lo guardado, como borrador.
 *
 * `offer.page` puede faltar —un servidor anterior a estas columnas no lo manda—
 * y entonces valen los mismos defectos que la base: todo vacío, indexable y en
 * el sitemap. Sin esto la pestaña se dibujaría en blanco contra `undefined`.
 */
function draftOf(offer: Offer): Draft {
  const page = offer.page
  return {
    slug: offer.slug,
    pageSeoTitle: page?.seoTitle ?? '',
    pageSeoDescription: page?.seoDescription ?? '',
    pageCanonicalUrl: page?.canonicalUrl ?? '',
    pageOgSameAsSeo: page?.ogSameAsSeo ?? true,
    pageOgTitle: page?.ogTitle ?? '',
    pageOgDescription: page?.ogDescription ?? '',
    pageOgImage: page?.ogImage ?? '',
    pageIndexable: page?.indexable ?? true,
    pageInSitemap: page?.inSitemap ?? true,
  }
}

export function OfferPageSettings({ projectId, token, offer, siteOrigin, onChange }: {
  projectId: string
  token: string
  offer: Offer
  /** El dominio público del sitio, o `null` si todavía no se ha publicado. */
  siteOrigin?: string | null | undefined
  /** La oferta que devuelve el servidor tras guardar; la lista vive fuera. */
  onChange: (offer: Offer) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()

  const [saved, setSaved] = useState<Draft>(() => draftOf(offer))
  const [form, setForm] = useState<Draft>(saved)
  const [saving, setSaving] = useState(false)
  const [attention, setAttention] = useState(0)
  const [copied, setCopied] = useState(false)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  /* Si la oferta cambia por fuera —se publica desde la cabecera, se edita en el
     asistente— el borrador se rehace. Con cambios sin guardar no: pisar lo que
     alguien está escribiendo por un guardado que no es suyo es la peor forma de
     perder un texto. */
  const dirty = useMemo(
    () => (Object.keys(form) as (keyof Draft)[]).some((key) => form[key] !== saved[key]),
    [form, saved],
  )
  useEffect(() => {
    const next = draftOf(offer)
    setSaved(next)
    if (!dirty) setForm(next)
    // Con `dirty` fuera a propósito: esto reacciona a la oferta, no a que se
    // escriba en el formulario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer])

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1600)
    return () => window.clearTimeout(timer)
  }, [copied])

  const patch = (values: Partial<Draft>) => setForm((current) => ({ ...current, ...values }))

  /*
   * Por qué puerta se entra a esta oferta. Las tres las sirve el worker y las
   * tres leen estos ajustes; lo único que cambia es el camino.
   */
  const isBookable = offer.booking.durationMinutes !== null
  const prefix = isBookable
    ? '/book/'
    : isFree(offer) ? '/checkout/free/' : '/checkout/buy/'
  const publicUrl = siteOrigin ? `${siteOrigin}${prefix}${form.slug}` : null
  /* Sin sitio publicado todavía no hay dominio: se enseña uno de ejemplo, que
     dice la forma sin prometer una dirección que aún no existe. */
  const host = siteOrigin ? siteOrigin.replace(/^https?:\/\//, '') : t('offers.detail.slugHostExample')

  /* Lo que se va a usar de verdad, que es lo que enseñan los marcadores de
     posición y la vista previa: la misma cadena de respaldos que imprime el
     servidor. */
  const effectiveTitle = form.pageSeoTitle.trim() || offer.title
  const effectiveDescription = form.pageSeoDescription.trim() || inheritedDescription(offer.description)
  const shareTitle = form.pageOgSameAsSeo ? effectiveTitle : (form.pageOgTitle.trim() || effectiveTitle)
  const shareDescription = form.pageOgSameAsSeo
    ? effectiveDescription
    : (form.pageOgDescription.trim() || effectiveDescription)
  const shareImage = (form.pageOgSameAsSeo ? '' : form.pageOgImage) || offer.images[0] || ''

  const copyLink = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
    } catch {
      showToast(t('common.copyFailed'), 'error')
    }
  }

  /**
   * Guarda, y dice la verdad sobre la dirección.
   *
   * La dirección que se manda es una PETICIÓN: el servidor la normaliza —fuera
   * acentos, espacios y mayúsculas— y la hace única dentro del proyecto, así que
   * puede volver con un sufijo. Se pinta lo que conteste y, si no es lo que se
   * pidió, se avisa: callarlo dejaría a alguien creyendo que su enlace es otro.
   */
  const save = async () => {
    setSaving(true)
    const wanted = form.slug.trim().replace(/^\/+/, '')
    try {
      const body: Parameters<typeof updateOffer>[2] = {
        pageSeoTitle: form.pageSeoTitle.trim(),
        pageSeoDescription: form.pageSeoDescription.trim(),
        pageCanonicalUrl: form.pageCanonicalUrl.trim(),
        pageOgSameAsSeo: form.pageOgSameAsSeo,
        pageOgTitle: form.pageOgTitle.trim(),
        pageOgDescription: form.pageOgDescription.trim(),
        pageOgImage: form.pageOgImage.trim(),
        pageIndexable: form.pageIndexable,
        /* Tal cual, aunque el indexado esté apagado y el interruptor no se vea:
           es lo que esta persona decidió, y quien vuelva a encender el indexado
           tiene que encontrarlo como lo dejó. Que no salga en el sitemap
           mientras tanto lo garantiza el servidor, que exige las dos. */
        pageInSitemap: form.pageInSitemap,
        /* La dirección sólo si de verdad cambió: pedirle al servidor que la deje
           como estaba es una petición que sólo puede fallar. */
        ...(wanted && wanted !== saved.slug ? { slug: wanted } : {}),
      }
      const result = await updateOffer(projectId, offer.id, body, token)
      onChange(result)
      setSaved(draftOf(result))
      setForm(draftOf(result))
      if (wanted && wanted !== saved.slug && result.slug !== wanted) {
        showToast(t('offers.detail.slugAdjusted', { slug: result.slug }))
      } else {
        showToast(t('offers.page.saved'))
      }
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="dash-project-general-content">
      <PendingChangesActions
        dirty={dirty}
        saving={saving}
        attentionKey={attention}
        onDiscard={() => setForm(saved)}
        onSave={() => { void save() }}
      />

      {/* Dirección */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconWorld size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('offers.page.address')}</h3>
            <p>{t('offers.page.slugHint')}</p>
          </div>
        </div>
        <label className="dash-field">
          <span>{t('pageSettings.slug')}</span>
          <div className="dash-page-settings-url">
            <div className="dash-page-settings-url-box">
              <small title={`${host}${prefix}`}>{host}{prefix}</small>
              <input
                value={form.slug}
                maxLength={80}
                aria-label={t('offers.detail.slug')}
                onChange={(event) => patch({ slug: event.target.value })}
              />
            </div>
            <button
              className="dash-secondary-btn"
              type="button"
              disabled={!publicUrl}
              onClick={() => { void copyLink() }}
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              <span>{t(copied ? 'offers.detail.link.copied' : publicUrl ? 'offers.detail.link.copy' : 'pageSettings.unpublished')}</span>
            </button>
          </div>
        </label>
      </section>

      {/* Buscadores (SEO) */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconSearch size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('offers.page.seo')}</h3>
            <p>{t('offers.page.seoBody')}</p>
          </div>
        </div>
        <label className="dash-field">
          <span>{t('pageSettings.seoTitle')} <InfoHint text={t('pageSettings.seoTitleHint')} /></span>
          <input
            className="dash-placeholder-strong"
            value={form.pageSeoTitle}
            placeholder={offer.title}
            onChange={(event) => patch({ pageSeoTitle: event.target.value })}
          />
          {form.pageSeoTitle ? (
            <small className="dash-page-settings-hint">
              {t('pageSettings.seoChars', { count: form.pageSeoTitle.length, max: TITLE_LIMIT })}
            </small>
          ) : null}
        </label>
        <label className="dash-field">
          <span>{t('pageSettings.seoDesc')} <InfoHint text={t('pageSettings.seoDescHint')} /></span>
          <textarea
            rows={3}
            className="dash-placeholder-strong"
            value={form.pageSeoDescription}
            placeholder={inheritedDescription(offer.description) || t('pageSettings.seoDescPlaceholder')}
            onChange={(event) => patch({ pageSeoDescription: event.target.value })}
          />
          {form.pageSeoDescription ? (
            <small className="dash-page-settings-hint">
              {t('pageSettings.seoChars', { count: form.pageSeoDescription.length, max: DESCRIPTION_LIMIT })}
            </small>
          ) : null}
        </label>
        <label className="dash-field">
          <span>{t('pageSettings.canonical')} <InfoHint text={t('offers.page.canonicalHint')} /></span>
          <input
            value={form.pageCanonicalUrl}
            placeholder={publicUrl ?? `${host}${prefix}${form.slug}`}
            onChange={(event) => patch({ pageCanonicalUrl: event.target.value })}
          />
        </label>
      </section>

      {/* Compartir */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconPhoto size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('pageSettings.share')}</h3>
            <p>{t('offers.page.shareBody')}</p>
          </div>
        </div>
        <div className="dash-page-settings-og">
          <button
            type="button"
            className={`dash-page-settings-og-image${shareImage ? ' has-image' : ''}`}
            title={t(shareImage ? 'pageSettings.changeCover' : 'pageSettings.uploadCover')}
            onClick={() => setImagePickerOpen(true)}
          >
            {shareImage ? (
              <>
                <img src={shareImage} alt="" />
                <span className="dash-record-hero-cover-mark" aria-hidden="true">
                  <IconPhotoPlus size={18} />
                  <span>{t('common.changeImage')}</span>
                </span>
              </>
            ) : <IconPhoto size={24} />}
          </button>
          <div className="dash-page-settings-og-copy">
            <small>{host}</small>
            <strong>{shareTitle}</strong>
            {shareDescription ? <p>{shareDescription}</p> : null}
          </div>
        </div>
        <p className="dash-page-settings-hint">
          {t(form.pageOgImage && !form.pageOgSameAsSeo ? 'offers.page.customImage' : 'offers.page.coverImage')}
          {form.pageOgImage && !form.pageOgSameAsSeo ? (
            <button type="button" className="dash-ofd-page-inline-btn" onClick={() => patch({ pageOgImage: '' })}>
              {t('offers.page.useCover')}
            </button>
          ) : null}
        </p>
        <ToggleRow
          icon={<IconFileText size={15} />}
          title={t('pageSettings.sameAsSeo')}
          description={t('pageSettings.sameAsSeoBody')}
          checked={form.pageOgSameAsSeo}
          onChange={(same) => patch({ pageOgSameAsSeo: same })}
        />
        {form.pageOgSameAsSeo ? null : (
          <>
            <label className="dash-field">
              <span>{t('pageSettings.ogTitle')} <InfoHint text={t('pageSettings.ogTitleHint')} /></span>
              <input
                className="dash-placeholder-strong"
                value={form.pageOgTitle}
                placeholder={effectiveTitle}
                onChange={(event) => patch({ pageOgTitle: event.target.value })}
              />
            </label>
            <label className="dash-field">
              <span>{t('pageSettings.ogDesc')} <InfoHint text={t('pageSettings.ogDescHint')} /></span>
              <textarea
                rows={3}
                className="dash-placeholder-strong"
                value={form.pageOgDescription}
                placeholder={effectiveDescription}
                onChange={(event) => patch({ pageOgDescription: event.target.value })}
              />
            </label>
          </>
        )}
      </section>

      {/* Visibilidad */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconWorld size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('pageSettings.visibility')}</h3>
            <p>{t('offers.page.visibilityBody')}</p>
          </div>
        </div>
        <ToggleRow
          icon={<IconSearch size={15} />}
          title={t('pageSettings.index')}
          description={t('offers.page.indexBody')}
          checked={form.pageIndexable}
          onChange={(pageIndexable) => patch({ pageIndexable })}
        />
        {form.pageIndexable ? (
          <ToggleRow
            icon={<IconFileText size={15} />}
            title={t('pageSettings.sitemap')}
            description={t('offers.page.sitemapBody')}
            checked={form.pageInSitemap}
            onChange={(pageInSitemap) => patch({ pageInSitemap })}
          />
        ) : null}
        {offer.status === 'active' ? null : (
          <small className="dash-page-settings-hint">{t('offers.page.draftNote')}</small>
        )}
      </section>

      {imagePickerOpen ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => {
            patch({ pageOgImage: url, pageOgSameAsSeo: false })
            setImagePickerOpen(false)
            setAttention((count) => count + 1)
          }}
          onClose={() => setImagePickerOpen(false)}
        />
      ) : null}
    </div>
  )
}
